// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * modes.ts — three shapes of a round, real spread not dials (principle #14).
 *
 * Each mode changes how you *play*, not just a number:
 *   Ascent  — normal plinth, unlimited bricks, RACE to a high line. Fast and
 *             loose: a topple only costs rebuild time, so you push.
 *   Salvage — normal plinth, a FIXED budget of drops, no line, max height. Every
 *             brick that slides off is a wasted drop, so you build carefully.
 *   Spire   — a plinth barely wider than a brick. A wide base topples at once, so
 *             balance is everything — this inverts the wide-base strategy the
 *             other two tolerate. Endless until it all comes down; scored on your
 *             tallest moment.
 *
 * All measurements are in CELLS (one cell = the unit a brick is built from).
 * plinthW is in cells; the physics layer multiplies by CELL for world units.
 */

export type EndKind = 'goal' | 'budget' | 'collapse';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Plinth width in cells. */
  plinthW: number;
  /** How the round ends. */
  end: EndKind;
  /** goal: height in cells a settled brick must cross to win. */
  goalCells: number;
  /** budget: number of drops allowed (Salvage). Also a hard cap for the others. */
  budget: number;
  /** collapse: end when height falls below this fraction of the max reached. */
  collapseFrac: number;
}

export const MODES: readonly Mode[] = [
  {
    id: 'ascent',
    name: 'Ascent',
    blurb: 'Unlimited bricks — race to the height line. A topple just costs you time.',
    plinthW: 4.4,
    end: 'goal',
    goalCells: 7,
    budget: 40,
    collapseFrac: 0,
  },
  {
    id: 'salvage',
    name: 'Salvage',
    blurb: 'Twelve bricks, no line. Every one that slides off is gone — build tall, build safe.',
    plinthW: 4.4,
    end: 'budget',
    goalCells: 0,
    budget: 12,
    collapseFrac: 0,
  },
  {
    id: 'spire',
    name: 'Spire',
    blurb: 'The plinth is barely a brick wide. No safe base — balance is the whole game.',
    plinthW: 1.5,
    end: 'collapse',
    goalCells: 0,
    budget: 40,
    collapseFrac: 0.4,
  },
];

export const DEFAULT_MODE = MODES[0];

/** Validate an id off the wire — an unknown id falls back, never reaches the sim. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string') {
    const m = MODES.find((x) => x.id === id);
    if (m) return m;
  }
  return DEFAULT_MODE;
}